import React, { useMemo } from "react";
import clsx from "clsx";

import { Context } from "../../../../../../teacherStore";
import { IActiveLearningIssue } from "../../../../../../state";
import {
  getStudentAnswersByQuestion,
  getActiveLearningAnswers,
} from "../../../../../../libs/ActiveLearning";
import AnswerStats from "../../../../../../atoms/ActiveLearning/AnswerStats";
import StudentAnswer from "../../../../../../molecules/ActiveLearning/StudentAnswer";
import styles from "../../../ActiveLearningIssue.module.scss";
import stylesAnswer from "../../../ActiveLearningAnswer.module.scss";

type Issue3AnswerProps = {
  materialCode: string;
  lessonNo: number;
  issue?: IActiveLearningIssue | undefined;
};

export const Issue3Answer = (props: Issue3AnswerProps) => {
  const { state } = React.useContext(Context);
  const currentIssueNo = 3;

  const question1 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 1);
  }, [props.issue?.questions]);

  const question2 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 2);
  }, [props.issue?.questions]);

  const question3 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 3);
  }, [props.issue?.questions]);

  const answersSortedByStudentName = useMemo(() => {
    return state.active_learning_answers?.sort((a, b) => {
      return a.last_name_kana.localeCompare(b.last_name_kana, "ja");
    });
  }, [state.active_learning_answers]);

  const allStudents = state.classes_by_grade.flatMap(
    classes => classes.students,
  );

  const allStudentAnswersI2Q1 = getActiveLearningAnswers(
    state.active_learning_material_answers,
    props.lessonNo,
    2,
    1,
  );

  const studentAnswersI2Q1 = useMemo(() => {
    const toStudentAnswers = answersSortedByStudentName?.map(student => {
      const answer = allStudentAnswersI2Q1?.filter(
        answer => answer.to_student_id === student.student_id,
      );
      return {
        ...student,
        answers: answer,
      };
    });

    const receivedAnswers = toStudentAnswers?.map(studentAnswers => {
      const fromStudentAnswers = studentAnswers.answers?.map(answer => {
        const fromAnswer = allStudents.find(
          student => student.id === answer.from_student_id,
        );
        return {
          ...answer,
          id: fromAnswer?.id,
          full_name: `${fromAnswer?.last_name} ${fromAnswer?.first_name}`,
        };
      });
      return {
        ...studentAnswers,
        answers: fromStudentAnswers || [],
      };
    });

    return receivedAnswers;
  }, [answersSortedByStudentName, allStudents, allStudentAnswersI2Q1]);

  const studentAnswersQ1 = useMemo(() => {
    return answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswers = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        1,
      );

      return {
        ...studentAnswers,
        answers: filteredAnswers,
      };
    });
  }, [answersSortedByStudentName, props.materialCode, props.lessonNo]);

  const studentAnswerStatsQ1 = useMemo(() => {
    const answered =
      studentAnswersQ1?.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersQ1?.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersQ1]);

  const studentAnswersQ2 = useMemo(() => {
    return answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswers = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        2,
      );

      return {
        ...studentAnswers,
        answers: filteredAnswers,
      };
    });
  }, [answersSortedByStudentName, props.materialCode, props.lessonNo]);

  const studentAnswerStatsQ2 = useMemo(() => {
    const answered =
      studentAnswersQ2?.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersQ2?.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersQ2]);

  const studentAnswersQ3 = useMemo(() => {
    return answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswers = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        3,
      );

      return {
        ...studentAnswers,
        answers: filteredAnswers,
      };
    });
  }, [answersSortedByStudentName, props.materialCode, props.lessonNo]);

  const studentAnswerStatsQ3 = useMemo(() => {
    const answered =
      studentAnswersQ3?.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersQ3?.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersQ3]);

  return (
    <div key={props.issue?.no} className={styles.issue}>
      <div className={clsx(styles.question, styles.primary)}>
        <div className={styles.header}>
          <span className={styles.no}>
            WORK
            {props.issue?.no && props.issue?.no < 10
              ? `0${props.issue?.no}`
              : props.issue?.no}
          </span>
          <p className={styles.title}>{props.issue?.title}</p>
          <p className={styles.read}>もらったコメント</p>
        </div>
        <div className={styles.answersArea}>
          {studentAnswersI2Q1?.map((studentAnswers, index) => {
            const no = index + 1;
            const displayNo = no < 10 ? `0${no}` : `${no}`;
            const isAnswered = studentAnswers.answers.length > 0;
            return (
              <StudentAnswer
                key={studentAnswers.student_id}
                isAnswered={isAnswered}
                isAccordion
                no={displayNo}
                name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                classId={studentAnswers.class_id}
                studentId={studentAnswers.student_id}
              >
                {isAnswered && (
                  <div className={styles.answersWrap}>
                    {studentAnswers.answers.map(answers => (
                      <div key={answers.id} className={stylesAnswer.tree}>
                        <p className={stylesAnswer.answer}>
                          {answers.full_name}さん
                        </p>
                        <ul className={stylesAnswer.branchList}>
                          <li className={stylesAnswer.branch}>
                            <div className={stylesAnswer.labelWrapper}>
                              <span className={stylesAnswer.label}>
                                コメント
                              </span>
                            </div>
                            <ul className={stylesAnswer.answer}>
                              <li className={styles.item}>{answers.value}</li>
                            </ul>
                          </li>
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </StudentAnswer>
            );
          })}
        </div>
        {question1 && (
          <>
            <div className={styles.themEntry}>
              <p className={styles.title}>{question1.title}</p>
              <AnswerStats
                answered={studentAnswerStatsQ1.answered}
                total={studentAnswerStatsQ1.total}
                unanswered={studentAnswerStatsQ1.unanswered}
              />
            </div>
            <div className={styles.answersArea}>
              {studentAnswersQ1?.map((studentAnswers, index) => {
                const no = index + 1;
                const displayNo = no < 10 ? `0${no}` : `${no}`;
                const isAnswered = studentAnswers.answers.length > 0;
                return (
                  <StudentAnswer
                    key={studentAnswers.student_id}
                    isAnswered={isAnswered}
                    isAccordion
                    no={displayNo}
                    name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                    classId={studentAnswers.class_id}
                    studentId={studentAnswers.student_id}
                  >
                    {isAnswered &&
                      studentAnswers.answers.map(answer => (
                        <div key={answer.id} className={stylesAnswer.multiple}>
                          <div className={stylesAnswer.block}>
                            <p className={stylesAnswer.label}>良かった点</p>
                            <p className={stylesAnswer.answer}>
                              {answer.contents.good}
                            </p>
                          </div>
                          <div className={stylesAnswer.block}>
                            <p className={stylesAnswer.label}>改善点</p>
                            <p className={stylesAnswer.answer}>
                              {answer.contents.motto}
                            </p>
                          </div>
                        </div>
                      ))}
                  </StudentAnswer>
                );
              })}
            </div>
          </>
        )}
        {question2 && (
          <>
            <div className={styles.themEntry}>
              <p className={styles.title}>{question2.title}</p>
              <AnswerStats
                answered={studentAnswerStatsQ2.answered}
                total={studentAnswerStatsQ2.total}
                unanswered={studentAnswerStatsQ2.unanswered}
              />
            </div>
            <div className={styles.answersArea}>
              {studentAnswersQ2?.map((studentAnswers, index) => {
                const no = index + 1;
                const displayNo = no < 10 ? `0${no}` : `${no}`;
                const isAnswered = studentAnswers.answers.length > 0;
                return (
                  <StudentAnswer
                    key={studentAnswers.student_id}
                    isAnswered={isAnswered}
                    isAccordion
                    no={displayNo}
                    name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                    classId={studentAnswers.class_id}
                    studentId={studentAnswers.student_id}
                  >
                    {isAnswered &&
                      studentAnswers.answers.map(answer => (
                        <div key={answer.id} className={stylesAnswer.multiple}>
                          <div className={stylesAnswer.block}>
                            <p className={stylesAnswer.label}>良かった点</p>
                            <p className={stylesAnswer.answer}>
                              {answer.contents.good}
                            </p>
                          </div>
                          <div className={stylesAnswer.block}>
                            <p className={stylesAnswer.label}>改善点</p>
                            <p className={stylesAnswer.answer}>
                              {answer.contents.motto}
                            </p>
                          </div>
                        </div>
                      ))}
                  </StudentAnswer>
                );
              })}
            </div>
          </>
        )}
        {question3 && (
          <>
            <div className={styles.themEntry}>
              <p className={styles.title}>{question3.title}</p>
              <AnswerStats
                answered={studentAnswerStatsQ3.answered}
                total={studentAnswerStatsQ3.total}
                unanswered={studentAnswerStatsQ3.unanswered}
              />
            </div>
            <div className={styles.answersArea}>
              {studentAnswersQ3?.map((studentAnswers, index) => {
                const no = index + 1;
                const displayNo = no < 10 ? `0${no}` : `${no}`;
                const isAnswered = studentAnswers.answers.length > 0;
                return (
                  <StudentAnswer
                    key={studentAnswers.student_id}
                    isAnswered={isAnswered}
                    isAccordion
                    no={displayNo}
                    name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                    classId={studentAnswers.class_id}
                    studentId={studentAnswers.student_id}
                  >
                    {isAnswered &&
                      studentAnswers.answers.map(answer => (
                        <div key={answer.id} className={stylesAnswer.multiple}>
                          <div className={stylesAnswer.block}>
                            <p className={stylesAnswer.label}>良かった点</p>
                            <p className={stylesAnswer.answer}>
                              {answer.contents.good}
                            </p>
                          </div>
                          <div className={stylesAnswer.block}>
                            <p className={stylesAnswer.label}>改善点</p>
                            <p className={stylesAnswer.answer}>
                              {answer.contents.motto}
                            </p>
                          </div>
                        </div>
                      ))}
                  </StudentAnswer>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default Issue3Answer;
